import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import { useForm, Controller } from 'react-hook-form';
import { usPhoneNumberValidation } from 'validators';
import { GridItem, Grid, TextField, Tooltip, USPhoneNumberInput, ConditionalWrapper, useIsInBottomSheet, useOverlayContext } from 'seagull';
import { validateContact } from '~/business-logic/validators';
import { ResponsiveModal } from '~/client-flow/components/ResponsiveModal';
import { useResponsive } from '~/client-flow/contexts';
import { useEventReporter } from '~/event-reporter';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { MANAGE_CONTACT_MODAL_TEXTS } from './ManageContactModal.constants';
import styles from './ManageContactModal.module.scss';
export var ContactModalType = /*#__PURE__*/ function(ContactModalType) {
    ContactModalType["Edit"] = "edit";
    ContactModalType["Create"] = "create";
    return ContactModalType;
}({});
export function ManageContactModal(props) {
    const { onClose, contact, modalType, onSaveContact, onCancelClick, origin, extraCellsContent, editingLimitations } = props;
    const { logBI } = useStoreActions();
    const { showSuccessToast } = useOverlayContext();
    const { reportToGtm } = useEventReporter();
    const { isDesktop } = useResponsive();
    const isInBottomSheet = useIsInBottomSheet();
    const usPhoneValidator = usPhoneNumberValidation();
    const [disableSave, setDisableSave] = useState(false);
    const { t: manageContactModalTexts } = useLocaleKeys('components.manageContactModal');
    const { t: contactTexts } = useLocaleKeys('components.manageContactModal');
    const { handleSubmit, control } = useForm({
        mode: 'all',
        defaultValues: {
            companyName: (contact === null || contact === void 0 ? void 0 : contact.companyName) || '',
            email: (contact === null || contact === void 0 ? void 0 : contact.email) || '',
            firstName: (contact === null || contact === void 0 ? void 0 : contact.firstName) || '',
            lastName: (contact === null || contact === void 0 ? void 0 : contact.lastName) || '',
            phone: (contact === null || contact === void 0 ? void 0 : contact.phone) || ''
        }
    });
    const onSecondaryButtonClick = async ()=>{
        const shouldCloseModal = await onCancelClick();
        if (shouldCloseModal) return onClose();
    };
    const saveContact = debounce(async (data)=>{
        setDisableSave(true);
        const contactToSave = _object_spread_props(_object_spread({}, contact, data), {
            phone: data.phone || null
        });
        logBI(11601, {
            contact: contactToSave,
            origin
        });
        reportToGtm('form-success', 'contacts-table-clicked-save');
        const savedContactData = await onSaveContact(contactToSave);
        const isNew = modalType === "create";
        if (savedContactData) {
            onClose(_object_spread_props(_object_spread({}, savedContactData), {
                contactData: contactToSave,
                isNew
            }));
            showSuccessToast(isNew ? contactTexts.contactCreated() : contactTexts.contactUpdated(), isNew ? 'contact-created-toast' : 'contact-updated-toast');
        }
        setDisableSave(false);
    }, 100);
    const partialModalProps = useMemo(()=>{
        switch(modalType){
            case "create":
                {
                    return {
                        title: manageContactModalTexts.createTitle(),
                        primaryButtonLabel: manageContactModalTexts.createButton(),
                        secondaryButtonLabel: manageContactModalTexts.cancelButton()
                    };
                }
            case "edit":
                {
                    return {
                        title: manageContactModalTexts.editTitle(),
                        primaryButtonLabel: manageContactModalTexts.saveButton(),
                        secondaryButtonLabel: manageContactModalTexts.cancelButton()
                    };
                }
        }
    }, [
        modalType
    ]);
    const shouldDisableForServiceProvider = editingLimitations.hasServiceProviderBusiness;
    const defaultPhoneNumberHelperText = !shouldDisableForServiceProvider ? MANAGE_CONTACT_MODAL_TEXTS.helperText.phoneNumber : undefined;
    const hasServiceProviderTooltip = shouldDisableForServiceProvider ? MANAGE_CONTACT_MODAL_TEXTS.tooltips.serviceProvider : undefined;
    const shouldDisableForMultiVendor = editingLimitations.isMultiVendorClient;
    const multiVendorTooltip = shouldDisableForMultiVendor ? MANAGE_CONTACT_MODAL_TEXTS.tooltips.multiVendor : undefined;
    function fullWidthInBottomSheet(span) {
        return isInBottomSheet ? 12 : span;
    }
    return /*#__PURE__*/ _jsx(ResponsiveModal, _object_spread_props(_object_spread({
        biName: "manage_contact_modal",
        testId: props.testId || 'manage-contact-modal',
        isDesktop: isDesktop || isInBottomSheet,
        onClose: ()=>onClose(),
        onPrimaryButtonClick: handleSubmit(saveContact),
        primaryButtonDisabled: disableSave,
        onSecondaryButtonClick: onSecondaryButtonClick
    }, partialModalProps), {
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.container,
            children: /*#__PURE__*/ _jsxs(Grid, {
                rowGap: "medium",
                children: [
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: isDesktop ? 6 : 12,
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "firstName",
                            rules: {
                                validate: validateContact.firstName
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(ConditionalWrapper, {
                                    condition: Boolean(hasServiceProviderTooltip),
                                    wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                            testId: "disabled-first-name-tooltip",
                                            placement: "right",
                                            title: hasServiceProviderTooltip,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                children: children
                                            })
                                        }),
                                    children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'first_name'
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: manageContactModalTexts.firstName(),
                                        testId: "first-name",
                                        disabled: shouldDisableForServiceProvider
                                    }))
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: isDesktop ? 6 : 12,
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "lastName",
                            rules: {
                                validate: validateContact.lastName
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(ConditionalWrapper, {
                                    condition: Boolean(hasServiceProviderTooltip),
                                    wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                            testId: "disabled-last-name-tooltip",
                                            placement: "right",
                                            title: hasServiceProviderTooltip,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                children: children
                                            })
                                        }),
                                    children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'last_name'
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: manageContactModalTexts.lastName(),
                                        testId: "last-name",
                                        disabled: shouldDisableForServiceProvider
                                    }))
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: fullWidthInBottomSheet(extraCellsContent ? 6 : 12),
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "companyName",
                            rules: {
                                validate: validateContact.companyName
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(ConditionalWrapper, {
                                    condition: Boolean(multiVendorTooltip),
                                    wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                            testId: "disabled-company-name-tooltip",
                                            placement: "right",
                                            title: multiVendorTooltip,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                children: children
                                            })
                                        }),
                                    children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'company_name'
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: manageContactModalTexts.companyName(),
                                        testId: "company-name",
                                        disabled: shouldDisableForMultiVendor
                                    }))
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: fullWidthInBottomSheet(extraCellsContent ? 6 : 12),
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "email",
                            rules: {
                                validate: validateContact.email
                            },
                            render: (renderProps)=>/*#__PURE__*/ _jsx(ConditionalWrapper, {
                                    condition: Boolean(multiVendorTooltip),
                                    wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                            testId: "disabled-email-tooltip",
                                            placement: "right",
                                            title: multiVendorTooltip,
                                            children: children
                                        }),
                                    children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'email_address'
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: manageContactModalTexts.emailAddress(),
                                        testId: "email-address",
                                        disabled: shouldDisableForMultiVendor
                                    }))
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        span: fullWidthInBottomSheet((extraCellsContent === null || extraCellsContent === void 0 ? void 0 : extraCellsContent.length) === 1 ? 6 : 12),
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "phone",
                            rules: usPhoneValidator,
                            render: (renderProps)=>{
                                const connectedProps = connectControllerToTextField(renderProps);
                                return /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                    condition: Boolean(hasServiceProviderTooltip),
                                    wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                            testId: "disabled-phone-number-tooltip",
                                            placement: "right",
                                            title: hasServiceProviderTooltip,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                children: children
                                            })
                                        }),
                                    children: /*#__PURE__*/ _jsx(USPhoneNumberInput, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'phone_number'
                                        }
                                    }, connectedProps), {
                                        label: manageContactModalTexts.phoneNumber(),
                                        testId: "phone-number",
                                        helperText: connectedProps.helperText || defaultPhoneNumberHelperText,
                                        disabled: shouldDisableForServiceProvider
                                    }))
                                });
                            }
                        })
                    }),
                    extraCellsContent === null || extraCellsContent === void 0 ? void 0 : extraCellsContent.map((cellContent, index)=>/*#__PURE__*/ _jsx(GridItem, {
                            span: fullWidthInBottomSheet(6),
                            children: cellContent
                        }, index))
                ]
            })
        })
    }));
}
